import getCookie from "helpers/getCookie";
import setCookie from "helpers/setCookie";

/** Engage-specific query/cookie keys (not utm_* — avoids overlap with marketing attribution). */
export const ENGAGE_ATTRIBUTION_KEYS = ["ae_campaign_id", "ae_user_id", "ae_channel"] as const;

export type EngageAttributionKey = (typeof ENGAGE_ATTRIBUTION_KEYS)[number];

const ENGAGE_COOKIE_NAME = "initial_ae_params";

function parsePipeDelimitedParams(raw: string): Record<string, string> {
  const props: Record<string, string> = {};
  raw.split("|").forEach((segment) => {
    const [key, ...rest] = segment.split("=");
    const normalizedKey = (key || "").trim();
    const value = rest.join("=").trim();
    if (normalizedKey && value) {
      props[normalizedKey] = value;
    }
  });
  return props;
}

export function getEngageAttributionFromCookie(): Record<string, string> {
  const raw = getCookie(ENGAGE_COOKIE_NAME) || "";
  if (!raw) return {};
  return parsePipeDelimitedParams(raw);
}

export function getEngageAttributionFromUrl(): Record<string, string> {
  if (typeof window === "undefined") return {};

  const params = new URLSearchParams(window.location.search);
  const props: Record<string, string> = {};

  ENGAGE_ATTRIBUTION_KEYS.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) props[key] = value;
  });

  return props;
}

/** URL params override cookie (latest click wins). */
export function getEngageAttributionProps(): Record<string, string> {
  return {
    ...getEngageAttributionFromCookie(),
    ...getEngageAttributionFromUrl(),
  };
}

/** Persist Engage params from the current URL into `initial_ae_params` (first-touch style). */
export function persistEngageAttributionFromUrl(): void {
  if (typeof window === "undefined") return;

  const fromUrl = getEngageAttributionFromUrl();
  const keys = Object.keys(fromUrl);
  if (keys.length === 0) return;

  const existing = getEngageAttributionFromCookie();
  const merged = { ...existing, ...fromUrl };
  const segments = ENGAGE_ATTRIBUTION_KEYS.filter((k) => merged[k]).map((k) => `${k}=${merged[k]}`);

  if (segments.length === 0) return;

  const cookieValue = segments.join("|");
  const existingCookie = getCookie(ENGAGE_COOKIE_NAME);
  if (existingCookie === cookieValue) return;

  setCookie({ cname: ENGAGE_COOKIE_NAME, cvalue: cookieValue, exdays: 365 });
}
